import { useEffect, useState } from "react";
import { BACKEND_URL, supabase } from "@/lib/supabase";
import { apiFetch, ApiError } from "@/lib/api";
import { FetchEventSource } from "@/lib/sse";

export type MemoStatus = "idle" | "generating" | "ready" | "failed";

export interface MemoStreamState {
  status: MemoStatus;
  memoId: string | null;
  memoMarkdown: string | null;
  memoHtml: string | null;
  error: string | null;
}

interface MemoLookupResponse {
  memo_id?: string;
  memo_markdown?: string;
  memo_html?: string;
  conversation_id?: string;
}

function hasReadyMemo(memo: MemoLookupResponse | null): boolean {
  return Boolean((memo?.memo_markdown ?? "").trim() || (memo?.memo_html ?? "").trim());
}

/**
 * Suit l'état du mémo via SSE (events `memo_generating`, `memo_ready`, `memo_failed`)
 * sur le canal /api/chat/:conversationId/stream. Fallback : un GET initial pour
 * récupérer un mémo déjà persisté avant l'ouverture du flux.
 */
export function useMemoStream(conversationId: string | undefined, enabled = true): MemoStreamState {
  const [state, setState] = useState<MemoStreamState>({
    status: "idle",
    memoId: null,
    memoMarkdown: null,
    memoHtml: null,
    error: null,
  });

  useEffect(() => {
    if (!enabled || !conversationId) return;
    let cancelled = false;
    let es: FetchEventSource | null = null;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let pollStartDelay: ReturnType<typeof setTimeout> | null = null;
    let pollStartedAt = 0;
    const POLL_INTERVAL_MS = 4000;
    const POLL_START_DELAY_MS = 10_000;
    const POLL_MAX_MS = 10 * 60_000;

    const safeParse = (raw: string): Record<string, unknown> | null => {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    const stopPolling = () => {
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
      if (pollStartDelay) {
        clearTimeout(pollStartDelay);
        pollStartDelay = null;
      }
    };

    const pollOnce = async () => {
      if (cancelled) return;
      try {
        const res = await apiFetch<{ status?: string; memo_id?: string | null }>(
          `/api/chat/${conversationId}/memo-status`,
        );
        if (cancelled) return;
        if (res?.status === "ready") {
          setState((s) => ({
            ...s,
            status: "ready",
            memoId: res.memo_id ?? s.memoId ?? conversationId,
            error: null,
          }));
          stopPolling();
          es?.close();
          return;
        }
      } catch {
        // ignore — on retentera
      }
      if (cancelled) return;
      if (Date.now() - pollStartedAt >= POLL_MAX_MS) {
        stopPolling();
        return;
      }
      pollTimer = setTimeout(pollOnce, POLL_INTERVAL_MS);
    };

    const ensurePolling = (immediate = false) => {
      if (cancelled || pollTimer || pollStartDelay) return;
      pollStartedAt = Date.now();
      if (immediate) {
        void pollOnce();
      } else {
        pollStartDelay = setTimeout(() => {
          pollStartDelay = null;
          void pollOnce();
        }, POLL_START_DELAY_MS);
      }
    };

    (async () => {
      // 1) Lookup initial : un mémo peut déjà être prêt (refresh, navigation tardive).
      try {
        const memo = await apiFetch<MemoLookupResponse>(
          `/api/upsell/secretaire/memo/${conversationId}`,
        );
        if (cancelled) return;
        if (hasReadyMemo(memo)) {
          setState({
            status: "ready",
            memoId: memo.memo_id ?? conversationId,
            memoMarkdown: memo.memo_markdown ?? null,
            memoHtml: memo.memo_html ?? null,
            error: null,
          });
          return;
        }
      } catch (e) {
        const err = e as ApiError;
        if (cancelled) return;
        if (err?.status && err.status !== 404 && err.status !== 202) {
          // erreur non-bloquante : on continue sur SSE
        }
      }

      // 1bis) Filet de sécurité : on arme le poll memo-status dès le mount,
      // indépendamment du SSE. Si la conv est déjà marquée terminée côté
      // serveur, aucun event memo_generating n'arrivera — sans ce poll,
      // le bouton resterait figé. Le premier des deux à ramener "ready"
      // gagne et arrête l'autre.
      ensurePolling(true);

      // 2) Ouverture SSE pour suivre les transitions memo_*.
      const { data: sessData } = await supabase.auth.getSession();
      const token = sessData.session?.access_token;
      if (!token || cancelled) return;
      // fetch-SSE : JWT en en-tête Authorization, JAMAIS en query string (P0-quater ③ étendu
      // aux flux mémo/verdict ; le backend accepte déjà le header — verifyTokenFromQuery fallback).
      const url = `${BACKEND_URL}/api/public/bk/api/chat/${conversationId}/stream`;
      es = new FetchEventSource(url, { headers: { Authorization: `Bearer ${token}` } });

      es.addEventListener("memo_generating", () => {
        setState((s) => (s.status === "ready" ? s : { ...s, status: "generating", error: null }));
        ensurePolling(false);
      });

      es.addEventListener("memo_ready", (ev) => {
        const data = safeParse((ev as MessageEvent).data) ?? {};
        const memoId =
          (typeof data.memoId === "string" && data.memoId) ||
          (typeof data.memo_id === "string" && data.memo_id) ||
          (typeof data.id === "string" && data.id) ||
          conversationId;
        setState({
          status: "ready",
          memoId,
          memoMarkdown: typeof data.memo_markdown === "string" ? data.memo_markdown : null,
          memoHtml: typeof data.memo_html === "string" ? data.memo_html : null,
          error: null,
        });
        stopPolling();
        es?.close();
      });

      es.addEventListener("memo_failed", (ev) => {
        const data = safeParse((ev as MessageEvent).data) ?? {};
        const msg =
          (typeof data.message === "string" && data.message) ||
          (typeof data.error === "string" && data.error) ||
          "La génération du mémo a échoué.";
        setState((s) => ({ ...s, status: "failed", error: msg }));
      });

      // Filet de sécurité : si la connexion SSE se ferme/erreure pendant la
      // génération, on déclenche immédiatement le polling pour rattraper un
      // éventuel memo_ready manqué.
      es.addEventListener("error", () => {
        setState((s) => {
          if (s.status === "generating") ensurePolling(true);
          return s;
        });
      });
      // FetchEventSource signale les décrochages TRANSPORT via onerror (pas via un event « error ») :
      // même filet — le polling rattrape un memo_ready manqué.
      es.onerror = () => {
        setState((s) => {
          if (s.status === "generating") ensurePolling(true);
          return s;
        });
      };
    })();

    // Filet de sécurité : au retour de focus de l'onglet, on retente le lookup
    // initial pour rattraper un memo_ready raté pendant que l'onglet dormait.
    const onVisibility = () => {
      if (cancelled || document.visibilityState !== "visible") return;
      void (async () => {
        try {
          const memo = await apiFetch<MemoLookupResponse>(
            `/api/upsell/secretaire/memo/${conversationId}`,
          );
          if (cancelled) return;
          if (hasReadyMemo(memo)) {
            setState({
              status: "ready",
              memoId: memo.memo_id ?? conversationId,
              memoMarkdown: memo.memo_markdown ?? null,
              memoHtml: memo.memo_html ?? null,
              error: null,
            });
            stopPolling();
            es?.close();
          } else {
            ensurePolling(true);
          }
        } catch {
          // ignore
        }
      })();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibility);
      es?.close();
    };
  }, [conversationId, enabled]);


  return state;
}
